import React, { useState } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PLANS = [
    {
        id: 'starter',
        price: 9,
        credits: 200,
        name: 'Starter Pack',
        features: ['200 Credits', '~ $0.045 / hit cost', 'No Expiry', 'Email Support'],
        description: 'Perfect for hobbyists',
        validity: 'once' as const
    },
    {
        id: 'growth',
        price: 19,
        credits: 450,
        name: 'Growth Pack',
        features: ['450 Credits', '~ $0.042 / hit cost', 'No Expiry', 'Email Support'],
        description: 'Best value for creators',
        validity: 'once' as const
    },
    {
        id: 'enterprise',
        price: 99,
        credits: 999999999,
        name: 'Lifetime Membership',
        features: ['WhatsApp & Email Support', 'Integration Help', 'Team Support', 'Lifetime Membership'],
        description: 'High volume solution',
        popular: true,
        validity: 'lifetime' as const
    }
];

export function PaymentModal({ isOpen, onClose }: PaymentModalProps) {
    const { user, recordPayment, isPro } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingPlan, setPendingPlan] = useState<typeof PLANS[0] | null>(null);

    if (!isOpen) return null;

    const handlePurchaseClick = (plan: typeof PLANS[0]) => {
        setPendingPlan(plan);
        setShowConfirm(true);
    };

    const proceedWithPurchase = () => {
        if (!pendingPlan) return;

        // Restriction: Only PAYG users are blocked if they have an outstanding balance.
        // Credit pack users can change plans anytime.
        if (user?.planType === 'payg' && (user?.billing?.currentBalance || 0) > 0) {
            alert("Please settle your outstanding balance before changing plans.");
            return;
        }

        setShowConfirm(false);
        setLoading(true);
        const plan = pendingPlan;

        const amount = plan.price * 100; // Razorpay expects amount in paisa/cents depending on currency, assuming USD usually needs conversion but using number as simple unit here. 
        // NOTE: For INR with Razorpay, amount is in paise. If US based, its cents. 
        // Based on user prompt "29$, 49$, 89$", let's assume USD for display but Razorpay test usually supports INR by default. 
        // Let's stick to USD display but process as USD in options.

        const options = {
            key: import.meta.env.VITE_RAZORPAY_KEY_ID,
            amount: amount,
            currency: "USD",
            name: "BgRemover",
            description: `Purchase ${plan.name} Plan`,
            image: "https://bg.addy.ie/favicon.ico", // Using existing favicon as placeholder
            handler: async function (response: any) {
                try {
                    await recordPayment({
                        razorpay_payment_id: response.razorpay_payment_id,
                        amount: plan.price,
                        currency: "USD",
                        credits: plan.credits,
                        planName: plan.name,
                        validity: plan.validity,
                        planType: plan.validity === 'once' ? 'credit_pack' : 'payg'
                    });
                    onClose();
                    alert("Payment Successful! Your API Key has been generated/updated.");
                } catch (error) {
                    console.error("Payment recording failed", error);
                    alert("Payment successful but failed to record locally. Please contact support.");
                }
            },
            prefill: {
                name: user?.displayName,
                email: user?.email,
                contact: "" // Optional
            },
            theme: {
                color: "#2563EB"
            }
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.open();
        rzp.on('payment.failed', function (response: any) {
            alert("Payment Failed: " + response.error.description);
            setLoading(false);
            setPendingPlan(null);
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-background rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in duration-200 relative">

                {/* Confirmation Overlay */}
                {showConfirm && (
                    <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <div className="bg-card border-2 border-primary/20 p-6 rounded-xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200">
                            <div className="flex flex-col items-center text-center gap-4">
                                <div className="h-12 w-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                                    <AlertTriangle className="h-6 w-6 text-yellow-500" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-foreground">Change Plan?</h3>
                                    <p className="text-muted-foreground mt-2">
                                        Switching to <span className="text-foreground font-semibold">"{pendingPlan?.name}"</span> will
                                        reset your current credits and clear your usage logs history.
                                    </p>
                                    <p className="text-sm text-yellow-600 dark:text-yellow-500 mt-2 font-medium bg-yellow-500/10 px-3 py-1 rounded-full">
                                        This action cannot be undone.
                                    </p>
                                </div>
                                <div className="flex gap-3 w-full mt-2">
                                    <button
                                        onClick={() => setShowConfirm(false)}
                                        className="flex-1 px-4 py-2 border border-border rounded-lg text-foreground hover:bg-accent transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={proceedWithPurchase}
                                        className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 font-medium transition-colors"
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-background z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-foreground">Get Your API Key</h2>
                        <p className="text-muted-foreground">Choose a plan to activate your API access</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-accent rounded-full transition-colors">
                        <X className="h-6 w-6 text-muted-foreground" />
                    </button>
                </div>

                <div className="p-6 grid md:grid-cols-3 gap-6">
                    {PLANS.map((plan) => (
                        <div
                            key={plan.id}
                            className={`relative rounded-xl border-2 p-6 flex flex-col ${plan.popular
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50 bg-card'
                                } transition-all duration-200`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
                                    MOST POPULAR
                                </div>
                            )}

                            <div>
                                <h3 className="font-bold text-lg text-foreground">{plan.name}</h3>
                                <div className="mt-4 flex items-baseline gap-1">
                                    <span className="text-4xl font-bold text-foreground">${plan.price}</span>
                                </div>
                                <p className="mt-2 text-primary font-medium">
                                    {plan.id === 'enterprise' ? 'Membership + PAYG' : `${plan.credits} Credits`}
                                </p>
                            </div>

                            <ul className="mt-6 space-y-3 flex-1 mb-8">
                                {plan.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => handlePurchaseClick(plan)}
                                disabled={loading || (plan.id === 'enterprise' && user?.planType === 'payg')} // Disable if current plan
                                className={`w-full py-3 rounded-lg font-bold transition-all ${plan.id === 'enterprise' && user?.planType === 'payg'
                                    ? 'bg-green-600 text-white cursor-default' // Current plan style
                                    : plan.popular
                                        ? 'bg-primary text-primary-foreground hover:opacity-90 shadow-lg shadow-primary/25'
                                        : 'bg-accent text-accent-foreground hover:bg-accent/80'
                                    }`}
                            >
                                {loading && (plan.id !== 'enterprise' || user?.planType !== 'payg') ? 'Processing...' :
                                    (plan.id === 'enterprise' && user?.planType === 'payg') ? 'Current Plan' : `Buy ${plan.name}`}
                            </button>
                        </div>
                    ))}
                </div>

                <div className="p-4 bg-muted/50 text-center text-xs text-muted-foreground">
                    Secure payment via Razorpay. All plans include 30-day money-back guarantee.
                </div>
            </div>
        </div>
    );
}
